import {
  Calendar,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  IndianRupee,
  Search,
  ShoppingCart,
  Table as TableIcon,
} from "lucide-react";
import {
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import type React from "react";

export type BillDateFilter = "all" | "this-week" | "this-month" | "overdue";
export type BillAmountFilter = "all" | "above-one-lakh" | "below-one-lakh";
export type BillExportFormat = "csv" | "xlsx" | "pdf";

export interface BillVendorOption {
  id: string;
  name: string;
}

export interface BillFilterBarProps {
  search: string;
  onSearchChange: (next: string) => void;
  vendorId: string | null;
  vendorOptions: BillVendorOption[];
  onVendorIdChange: (next: string | null) => void;
  dateFilter: BillDateFilter;
  onDateFilterChange: (next: BillDateFilter) => void;
  amountFilter: BillAmountFilter;
  onAmountFilterChange: (next: BillAmountFilter) => void;
  /** Fired when the operator picks an export format. Omit to hide Export. */
  onExport?: (format: BillExportFormat) => void;
  /** The format currently exporting (drives the spinner label), or null. */
  exporting?: BillExportFormat | null;
}

export function BillFilterBar({
  search,
  onSearchChange,
  vendorId,
  vendorOptions,
  onVendorIdChange,
  dateFilter,
  onDateFilterChange,
  amountFilter,
  onAmountFilterChange,
  onExport,
  exporting = null,
}: BillFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative h-10 min-w-80 flex-1 lg:max-w-[38rem]">
        <Search
          size={15}
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <Input
          type="search"
          aria-label="Search vendor, bill number, GSTIN"
          placeholder="Search vendor, bill number, GSTIN…"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="h-10 pl-9"
        />
      </div>

      <FilterSelect
        value={vendorId ?? "all"}
        onValueChange={(next) => onVendorIdChange(next === "all" ? null : next)}
        icon={<ShoppingCart size={14} />}
        placeholder="Vendor"
        ariaLabel="Filter by vendor"
        widthClassName="!w-44"
      >
        <SelectItem value="all">All vendors</SelectItem>
        {vendorOptions.map((vendor) => (
          <SelectItem key={vendor.id} value={vendor.id}>
            {vendor.name}
          </SelectItem>
        ))}
      </FilterSelect>

      <FilterSelect
        value={dateFilter}
        onValueChange={(next) => onDateFilterChange(next as BillDateFilter)}
        icon={<Calendar size={14} />}
        placeholder="Date range"
        ariaLabel="Filter by date range"
        widthClassName="!w-44"
      >
        <SelectItem value="all">All dates</SelectItem>
        <SelectItem value="this-week">Due this week</SelectItem>
        <SelectItem value="this-month">Received this month</SelectItem>
        <SelectItem value="overdue">Overdue</SelectItem>
      </FilterSelect>

      <FilterSelect
        value={amountFilter}
        onValueChange={(next) => onAmountFilterChange(next as BillAmountFilter)}
        icon={<IndianRupee size={14} />}
        placeholder="Amount"
        ariaLabel="Filter by amount"
        widthClassName="!w-40"
      >
        <SelectItem value="all">All amounts</SelectItem>
        <SelectItem value="above-one-lakh">Above ₹1L</SelectItem>
        <SelectItem value="below-one-lakh">Below ₹1L</SelectItem>
      </FilterSelect>

      <span className="min-w-0 flex-1" />

      {onExport ? (
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              disabled={exporting !== null}
              title="Export the current view to CSV, Excel, or PDF"
              className="ml-auto"
            >
              <Download size={14} />
              <span>{exporting ? "Exporting…" : "Export"}</span>
              <ChevronDown size={13} aria-hidden className="text-muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Export bills</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => onExport("csv")}>
              <TableIcon size={13} aria-hidden className="text-muted" />
              CSV (.csv)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onExport("xlsx")}>
              <FileSpreadsheet size={13} aria-hidden className="text-muted" />
              Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onExport("pdf")}>
              <FileText size={13} aria-hidden className="text-muted" />
              PDF (.pdf)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      ) : null}
    </div>
  );
}

function FilterSelect({
  value,
  onValueChange,
  icon,
  placeholder,
  ariaLabel,
  widthClassName,
  children,
}: {
  value: string;
  onValueChange: (next: string) => void;
  icon: React.ReactNode;
  placeholder: string;
  ariaLabel: string;
  widthClassName: string;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label={ariaLabel} className={`h-10 shrink-0 gap-2 ${widthClassName}`}>
        <span aria-hidden className="text-muted">
          {icon}
        </span>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}
